import React, { useRef, useEffect, useState, useCallback } from "react";
import { ArrowRight, ChevronDown } from "lucide-react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1615529182904-14819c35db37?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const HERO_FALLBACK =
  "https://images.pexels.com/photos/6489092/pexels-photo-6489092.jpeg?auto=compress&cs=tinysrgb&w=2000";

const HeroSection = () => {
  const heroRef = useRef(null);
  const imgRef = useRef(null);
  const midRef = useRef(null);
  const textRef = useRef(null);
  const rafRef = useRef(0);
  const targetRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0 });

  const [scrollY, setScrollY] = useState(0);
  const [imgSrc, setImgSrc] = useState(HERO_IMAGE);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Detect reduced motion preference
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  // Scroll push-in
  useEffect(() => {
    const onScroll = () => {
      const el = heroRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        setScrollY(Math.max(0, -rect.top));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Mouse parallax — smooth interpolated with rAF + ambient auto-drift
  const startTimeRef = useRef(performance.now());
  const animate = useCallback(() => {
    const t = targetRef.current;
    const c = currentRef.current;
    // ease toward target
    c.x += (t.x - c.x) * 0.08;
    c.y += (t.y - c.y) * 0.08;

    // Ambient time-based drift — makes the scene feel alive even without mouse input
    const elapsed = (performance.now() - startTimeRef.current) / 1000;
    const ambX = Math.sin(elapsed * 0.35) * 0.55;   // slow horizontal sway
    const ambY = Math.cos(elapsed * 0.28) * 0.45;   // slower vertical breathing
    const ambScale = 1 + Math.sin(elapsed * 0.22) * 0.012; // breathing scale ±1.2%
    const ambRot = Math.sin(elapsed * 0.19) * 0.15; // gentle rotation degrees

    // combined offsets: ambient + mouse
    const bgX = (c.x + ambX) * 6;
    const bgY = (c.y + ambY) * 6 + scrollY * 0.22;
    const midX = (c.x + ambX) * -10;
    const midY = (c.y + ambY) * -10;
    const txtX = c.x * -2;
    const txtY = c.y * -2 + scrollY * -0.05;

    if (imgRef.current) {
      imgRef.current.style.transform =
        `translate3d(${bgX}px, ${bgY}px, 0) ` +
        `scale(${1.07 * ambScale}) ` +
        `rotateX(${c.y * -0.6}deg) rotateY(${c.x * 0.6 + ambRot}deg)`;
    }
    if (midRef.current) {
      midRef.current.style.transform =
        `translate3d(${midX}px, ${midY}px, 0)`;
    }
    if (textRef.current) {
      textRef.current.style.transform =
        `translate3d(${txtX}px, ${txtY}px, 0)`;
    }
    rafRef.current = requestAnimationFrame(animate);
  }, [scrollY]);

  useEffect(() => {
    if (reducedMotion) return;
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [animate, reducedMotion]);

  const onMouseMove = (e) => {
    if (reducedMotion) return;
    const el = heroRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Normalize -1..1
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    targetRef.current = { x: nx, y: ny };
  };

  const onMouseLeave = () => {
    targetRef.current = { x: 0, y: 0 };
  };

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const textShadow =
    "0 2px 12px rgba(59,25,48,0.55), 0 1px 3px rgba(0,0,0,0.35)";

  // Fade the whole hero content as it scrolls out (soft reveal for next section)
  const heroFade = Math.max(0, 1 - scrollY / 620);

  return (
    <section
      id="hero"
      ref={heroRef}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      className="relative w-full overflow-hidden"
      style={{
        backgroundColor: "#2A1620",
        height: "100vh",
        minHeight: "640px",
        perspective: "1400px",
        perspectiveOrigin: "50% 45%",
      }}
      data-testid="hero-section"
    >
      {/* Background image layer (deepest) */}
      <div
        className="absolute inset-0 z-0 overflow-hidden"
        style={{ transformStyle: "preserve-3d" }}
      >
        <img
          ref={imgRef}
          src={imgSrc}
          onError={() => setImgSrc(HERO_FALLBACK)}
          alt="Warm styled living room with home textiles, rattan pendant lamps and plants"
          className={reducedMotion ? "w-full h-full object-cover" : "w-full h-full object-cover hero-img-fadein"}
          style={{
            objectPosition: "center",
            willChange: "transform",
            transform: `translate3d(0, ${scrollY * 0.22}px, 0) scale(1.07)`,
            transformOrigin: "50% 50%",
          }}
          draggable={false}
          fetchPriority="high"
        />

        {/* Midground vignette layer — drifts opposite direction */}
        <div
          ref={midRef}
          className="absolute inset-0 pointer-events-none"
          style={{
            willChange: "transform",
            background:
              "radial-gradient(ellipse 70% 80% at 28% 55%, rgba(30,10,20,0.62) 0%, rgba(30,10,20,0.35) 40%, rgba(30,10,20,0) 75%)",
          }}
        />

        {/* Overall subtle scrim (stable) */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(30,10,20,0.15) 0%, rgba(30,10,20,0) 25%, rgba(30,10,20,0) 70%, rgba(30,10,20,0.28) 100%)",
          }}
        />
      </div>

      {/* Content (foreground, nearly stable) */}
      <div
        ref={textRef}
        className="relative z-10 max-w-[1440px] mx-auto h-full px-6 md:px-12 lg:px-20 flex items-center"
        style={{
          willChange: "transform",
          opacity: heroFade,
        }}
      >
        <div className="max-w-xl">
          <div
            className="hero-fade-warm mb-6 inline-flex items-center gap-2.5 text-[11px] md:text-[11.5px] font-semibold tracking-[0.36em] uppercase"
            style={{
              color: "#FBE9DA",
              animationDelay: "80ms",
              textShadow,
            }}
            data-testid="hero-eyebrow"
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full hero-dot-pulse"
              style={{ backgroundColor: "#F4C3CD" }}
            />
            Designed in Karur · Made for the world
          </div>

          <h1
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: "clamp(2.8rem, 5.6vw, 4.6rem)",
              fontWeight: 500,
              lineHeight: 1.02,
              letterSpacing: "-0.025em",
              color: "#FBF6EC",
              textShadow,
            }}
            data-testid="hero-headline"
          >
            <span
              className="hero-line-reveal block"
              style={{ animationDelay: "220ms" }}
            >
              Home textiles,
            </span>
            <span
              className="hero-line-reveal block"
              style={{
                fontStyle: "italic",
                fontWeight: 600,
                color: "#F4C3CD",
                textShadow,
                animationDelay: "440ms",
              }}
            >
              thoughtfully
            </span>
            <span
              className="hero-line-reveal block"
              style={{ animationDelay: "620ms" }}
            >
              made.
            </span>
          </h1>

          <p
            className="hero-fade-warm mt-6 leading-relaxed"
            style={{
              fontSize: "clamp(1rem, 1.15vw, 1.15rem)",
              maxWidth: "440px",
              color: "rgba(251,246,236,0.92)",
              textShadow: "0 1px 8px rgba(30,10,20,0.55)",
              animationDelay: "820ms",
            }}
            data-testid="hero-subtitle"
          >
            Vertically integrated. Design-led. Delivered to homes worldwide.
          </p>

          <div
            className="hero-fade-warm mt-9"
            style={{ animationDelay: "980ms" }}
          >
            <button
              type="button"
              onClick={() => scrollTo("#story")}
              className="hero-cta group inline-flex items-center gap-3 text-[13px] font-semibold tracking-[0.2em] uppercase pb-2.5 relative"
              style={{
                color: "#FBE9DA",
                textShadow: "0 1px 6px rgba(30,10,20,0.5)",
              }}
              data-testid="hero-cta-primary"
            >
              Discover the story
              <ArrowRight
                className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5"
                strokeWidth={2}
              />
              <span className="hero-cta-underline" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Scroll hint */}
      <button
        type="button"
        onClick={() => scrollTo("#story")}
        aria-label="Scroll to next section"
        className="absolute z-10 left-1/2 -translate-x-1/2 bottom-8 md:bottom-10 hero-scroll-hint inline-flex flex-col items-center gap-2 group"
        style={{ opacity: heroFade }}
        data-testid="hero-scroll-hint"
      >
        <span
          className="text-[10px] font-medium tracking-[0.42em] uppercase"
          style={{
            color: "rgba(251,246,236,0.85)",
            textShadow: "0 1px 6px rgba(30,10,20,0.55)",
          }}
        >
          Scroll
        </span>
        <span
          className="inline-flex items-center justify-center w-7 h-7 rounded-full transition-all duration-300 group-hover:-translate-y-0.5"
          style={{
            border: "1px solid rgba(251,246,236,0.55)",
            color: "#FBF6EC",
          }}
        >
          <ChevronDown className="w-3.5 h-3.5" strokeWidth={2.2} />
        </span>
      </button>
    </section>
  );
};

export default HeroSection;
